import { Sparkles } from "lucide-react";
import { useContext, useEffect, useState } from "react";

import AutoSuggestion from "@/app/components/AutoSuggestion";
import EditorListItems, { type EditorListItem } from "@/app/components/ResumeBuilder/EditorListItems/EditorListItems";
import TextEditor from "@/app/components/TextEditor";
import { Label } from "@/app/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/components/ui/popover";
import ResumeContext from "@/app/context/ResumeContext";
import { getJobFunctionFromTitle } from "@/app/seed/getMetadata";
import suggestionsData from "@/app/seed/metadata/filemapping";
import Titles from "@/app/seed/titles";
import type { ResumeContextValue } from "@/app/types/resume";

interface WorkDetailsEditorProps {
	/** Drives which responsibilities are suggested. */
	jobTitle: string;
	value: string;
	setValue: (value: string) => void;
	error?: string;
}

/** The responsibilities listed against a job function, deduplicated. */
const responsibilitiesFor = (jobFunction: string): EditorListItem[] => {
	// Whole value first: splitting on the comma turns "Advertising, Arts and
	// Media" into "advertising" and matches nothing.
	const suggestions = suggestionsData[jobFunction] || suggestionsData[jobFunction.split(",")[0].trim()] || [];
	const responsibilities = [
		...new Set(suggestions.flatMap((suggestion: { responsibilities: string[] }) => suggestion.responsibilities)),
	] as string[];

	return responsibilities.filter((title) => title !== "").map((title, id) => ({ id, title, added: false }));
};

/**
 * The achievements editor with its suggestions in a popover.
 *
 * The suggestions used to sit in a second column beside the editor, which is
 * what forced this step into an 80vw dialog of its own. In a popover they cost
 * the page no width, so the editor can live on the work history form.
 */
const WorkDetailsEditor = ({ jobTitle, value, setValue, error }: WorkDetailsEditorProps) => {
	const { resumeData } = useContext(ResumeContext) as ResumeContextValue;
	const [listItems, setListItems] = useState<EditorListItem[]>([]);
	// The title the list is drawn from. It follows the job title above, and the
	// search inside the popover borrows another role's wording when this job's own
	// suggestions are thin.
	const [searchedTitle, setSearchedTitle] = useState(jobTitle);

	useEffect(() => {
		setSearchedTitle(jobTitle);
	}, [jobTitle]);

	useEffect(() => {
		const matchedFunction = getJobFunctionFromTitle(searchedTitle).toString();
		const jobFunction = (matchedFunction || resumeData.contactInfo.resumeFunction.toString()).toLowerCase();

		setListItems(responsibilitiesFor(jobFunction));
	}, [searchedTitle, resumeData.contactInfo.resumeFunction]);

	return (
		<div>
			<div className="mb-1 flex items-center justify-between gap-3">
				<Label className="text-sm">Achievements and Responsibilities</Label>
				<Popover>
					<PopoverTrigger asChild>
						<button
							type="button"
							className="flex cursor-pointer items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 font-semibold text-primary-700 text-sm"
						>
							<Sparkles className="size-4" />
							Get help with writing
						</button>
					</PopoverTrigger>
					{/* Bounded by the room the trigger leaves on screen, so the list scrolls
					    inside the popover rather than the popover growing off the top of it. */}
					<PopoverContent
						align="end"
						collisionPadding={16}
						className="max-h-(--radix-popover-content-available-height) w-[min(32rem,92vw)] p-2"
					>
						<p className="px-1 text-muted-foreground text-xs">
							Pick the achievements and responsibilities that apply. They are added to your description.
						</p>
						<AutoSuggestion
							suggestions={Titles}
							userInput={searchedTitle}
							setUserInput={setSearchedTitle}
							isForm={false}
						/>
						<EditorListItems
							editorListItems={listItems}
							editorContent={value}
							setEditorContent={setValue}
							setEditorListItems={setListItems}
							className="h-auto min-h-0 flex-1"
						/>
					</PopoverContent>
				</Popover>
			</div>
			<TextEditor
				id="workDetails"
				value={value}
				setValue={setValue}
				placeholder="Use 3-5 bullet points to highlight your key achievements and responsibilities."
				className="text-editor-auto-grow"
			/>
			{error && <div className="mt-1 font-medium text-red-500 text-sm leading-tight">{error}</div>}
		</div>
	);
};

export default WorkDetailsEditor;
